import React, { useEffect, useState } from "react";
import { Card, Space, Flex } from "antd";
import { KeyOutlined, RightOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useThemeContext } from "../../../context/ThemeContext";
import PermissionsModal from "../../../components/common/PermissionsModal";
import ExpertMode from "../../../components/Settings/AppSettings/ExpertMode";
import apiInstance from "../../../services/apiService";
import ENDPOINTS from "../../../services/endpoint";
import SettingsSidebarHeader from "../../../components/common/SettingsSidebarHeader";
import CustomLoader from "../../../components/common/UI/Loader";
import GradientButton from "../../../components/common/UI/GradientButton";
import "./index.scss";

interface AppSettingsProps {
  handleBackToPrevious: () => void;
}

interface AppVersionDetails {
  createdAt: string;
  version: string;
  _id: string;
}

const AppSettings: React.FC<AppSettingsProps> = ({ handleBackToPrevious }) => {
  const { theme } = useThemeContext();
  const [currentModule, setCurrentModule] = useState<
    "Expert Mode" | "App Settings"
  >("App Settings");
  const [permissionsModalVisible, setPermissionsModalVisible] =
    useState<boolean>(false);
  const [appVersionDetails, setAppVersionDetails] =
    useState<AppVersionDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const options = [
    {
      id: 1,
      name: "App Permissions",
      number: "+971345678",
      icon: "Number",
    },
    {
      id: 2,
      name: "Expert Mode",
      icon: "schedule",
    },
    {
      id: 3,
      name: "App Version",
      icon: "Notificat",
      version: appVersionDetails?.version || (
        <CustomLoader height={14} width={4} />
      ),
    },
  ];

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case "Number":
        return (
          <img
            src={`/assets/icons/${theme}/incoming-call.png`}
            width={20}
            height={20}
          />
        );
      case "laptop":
        return (
          <img src="/assets/icons/Incomingescall.svg" width={20} height={20} />
        );
      case "schedule":
        return (
          <img
            src={`/assets/icons/${theme}/subscription.png`}
            width={20}
            height={20}
          />
        );

      case "Notificat":
        return (
          <img
            src={`/assets/icons/${theme}/app-version.png`}
            width={20}
            height={20}
          />
        );
      default:
        return <KeyOutlined style={{ fontSize: "18px" }} />;
    }
  };

  const handleAction = (id: number) => {
    if (id === 1) {
      setPermissionsModalVisible(true);
    } else if (id === 2) {
      setCurrentModule("Expert Mode");
    }
  };

  const handleBackClick = () => {
    if (currentModule === "Expert Mode") {
      setCurrentModule("App Settings");
    } else {
      handleBackToPrevious();
    }
  };

  useEffect(() => {
    const getAppVersion = async () => {
      setLoading(true);

      const url = ENDPOINTS.GET_VERSION;

      try {
        const response = await apiInstance.get(url);
        const data = response.data.data;
        setAppVersionDetails(data);
      } catch (error: any) {
        console.error(error);
        toast.error(error.message || "Couldn't get App version");
      } finally {
        setLoading(false);
      }
    };

    getAppVersion();
  }, []);

  return (
    <>
      <div className="app-settings-container">
        <SettingsSidebarHeader
          title="App Settings"
          handleBackClick={handleBackClick}
        />

        <div className="options-container">
          {currentModule === "App Settings" ? (
            options.map((item) => (
              <Card
                key={item.id}
                bordered={false}
                onClick={() => handleAction(item.id)}
                styles={{ body: { padding: "6px 12px " } }}
                className="option-card"
              >
                <Flex align="center" justify="space-between" gap={10}>
                  <Space size={6} className="title-container">
                    <GradientButton className="option-icon">
                      {getIconComponent(item.icon)}
                    </GradientButton>
                    <h6 className="option-title">{item.name}</h6>
                  </Space>
                  <div className="version">
                    {item.version ? (
                      loading ? (
                        <CustomLoader height={14} width={4} />
                      ) : (
                        `v${item.version}`
                      )
                    ) : (
                      <RightOutlined />
                    )}
                  </div>
                </Flex>
              </Card>
            ))
          ) : (
            <ExpertMode />
          )}
        </div>
      </div>

      <PermissionsModal
        visible={permissionsModalVisible}
        onClose={() => setPermissionsModalVisible(false)}
      />
    </>
  );
};

export default AppSettings;
